/* eslint-disable react/forbid-component-props */
/* eslint-disable react-hooks/set-state-in-effect */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { Headline, Tag, Text } from '@oneaudi/unified-web-alpha-components';
import useAnimations from '../../../hooks/useAnimations';
import { device } from '../../../styles/devices';
import FallbackCarDefault from '../../../assets/fallback_car_default.png';
import ExposedHeader from '../ExposedHeader';
import {
  MobileWrapper,
  VehicleInfoContainer,
  ImageWrapper,
  VehicleImage,
  VehicleName,
  Trimline,
  Title,
  OfferType,
} from '../VehicleInfo.styles';
import type { OffersTypes } from '../../../types';
import Offers from '../Offers';

interface OfferSpecificVehicleProps {
  offers: OffersTypes;
  isOfferPage: boolean;
  market: string;
}

const OfferSpecificVehicle: React.FC<OfferSpecificVehicleProps> = ({
  offers,
  isOfferPage,
  market,
}) => {
  const { t } = useTranslation();
  const isTablet = useMediaQuery({ query: device.tablet });
  const [isOpen, setIsOpen] = useState(false);

  const dropdownHeigh = 464;
  const { dropdownAnimation, filtersAccordianAnimation } = useAnimations(isOpen, dropdownHeigh);

  const image = offers?.image ? offers.image : FallbackCarDefault;
  const modelName = offers?.modelName;
  const trimName = offers?.trimName;

  const vehicleLabel = t('digitalLeads.vehicle.offerOfInterest');

  const isUsOffersPage = market === 'USA';

  useEffect(() => {
    if (isTablet) setIsOpen(false);
  }, [isTablet]);

  const renderVehicleInfo = useMemo(() => {
    if (process.env.IS_LEGACY) {
      return (
        <>
          <VehicleName data-testid="vehicle-info-name">{modelName}</VehicleName>
          <Trimline>{trimName}</Trimline>
        </>
      );
    }

    return (
      <>
        <Headline
          data-testid="vehicle-info-name"
          margin={['8px', 0, 0, 0]}
          variant={{ type: 'headline', order: '3', weight: 'normal' }}
          tag="h2"
        >
          {modelName}
        </Headline>
        <Text margin={['12px', 0, 0, 0]} variant={{ order: '1', style: 'normal' }} tag="p">
          {trimName}
        </Text>
      </>
    );
  }, [modelName, trimName]);

  return (
    <>
      {dropdownAnimation(
        (props, item) =>
          item && (
            <MobileWrapper style={props}>
              <VehicleInfoContainer style={filtersAccordianAnimation} data-testid="vehicle-info">
                <ImageWrapper data-testid="vehicle-info-image" isUsOffersPage={isUsOffersPage}>
                  <VehicleImage src={image} />
                </ImageWrapper>
                {renderVehicleInfo}
                <Offers offers={offers?.offerDetails} offerType={offers.offerType} />
              </VehicleInfoContainer>
            </MobileWrapper>
          ),
      )}
      {!isTablet && (
        <ExposedHeader
          isOpened={isOpen}
          setIsOpened={setIsOpen}
          modelName={modelName}
          isOfferPage={isOfferPage}
          offers={offers}
          vehicleLabel={vehicleLabel}
        />
      )}
      {isTablet && (
        <VehicleInfoContainer data-testid="vehicle-info-tablet">
          <ImageWrapper data-testid="vehicle-info-image" isUsOffersPage={isUsOffersPage}>
            <VehicleImage src={image} />
          </ImageWrapper>

          {process.env.IS_LEGACY ? (
            <Title paddingTop>{vehicleLabel}</Title>
          ) : (
            <Headline
              margin={['24px', 0, 0, 0]}
              variant={{ type: 'headline', order: '4', weight: 'normal' }}
              tag="h2"
            >
              {vehicleLabel}
            </Headline>
          )}

          {process.env.IS_LEGACY ? (
            <OfferType>{t(`digitalLeads.vehicle.offers.${offers?.offerType}`)}</OfferType>
          ) : (
            <Tag
              label={t(`digitalLeads.vehicle.offers.${offers?.offerType}`)}
              margin={[0, 0, 0, 0]}
              size="md"
              variant="read-only"
            />
          )}

          {renderVehicleInfo}
          <Offers offers={offers?.offerDetails} offerType={offers.offerType} />
        </VehicleInfoContainer>
      )}
    </>
  );
};

export default OfferSpecificVehicle;
